import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, CheckCircle2, ExternalLink } from "lucide-react";
import Section, { SectionHeader, FadeIn } from "@/components/Section";
import {
  BRAND,
  CAREER,
  COMPANIES,
  EDUCATION,
  LANGUAGES,
  INDUSTRIES,
  PRESS_ARTICLE,
} from "@/lib/brand";

const PRINCIPLES = [
  {
    title: "Klarheit vor Komfort",
    text: "Ich sage, was ich sehe. Höflich, aber unmissverständlich. Das spart allen Beteiligten Monate.",
  },
  {
    title: "Struktur statt Intuition",
    text: "Methodik und Werkzeuge. Keine Ratgeber-Phrasen. Jede Sitzung hat ein Ergebnis.",
  },
  {
    title: "Mensch vor Methode",
    text: "Werkzeuge sind wichtig. Die Person ist wichtiger. Coaching funktioniert nur auf Augenhöhe.",
  },
  {
    title: "Ergebnisse messbar machen",
    text: "Am Ende zählt, was sich im Alltag ändert. Sichtbar, nachvollziehbar, nachhaltig.",
  },
];

export default function AboutPage() {
  return (
    <div data-testid="about-page" className="pt-36 lg:pt-44">
      {/* Intro */}
      <Section padded={false} className="pb-24 lg:pb-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14">
          <div className="lg:col-span-7">
            <FadeIn>
              <div className="overline mb-6">Über mich</div>
              <h1 className="font-display text-[44px] sm:text-[60px] lg:text-[80px] leading-[0.98] tracking-[-0.035em] text-white">
                Zwanzig Jahre Berufswelt.
                <br />
                <span className="font-editorial italic text-white/90">
                  In ein Coaching übersetzt.
                </span>
              </h1>
              <p className="mt-8 text-white/65 text-lg lg:text-xl leading-relaxed max-w-xl">
                Ich bin {BRAND.name} — Jahrgang 1977, Coach, Unternehmer und
                mehrfacher Geschäftsführer. Mein Arbeitsfeld ist die Berufswelt:
                von der Ausbildung bis zur Geschäftsführungs-Ebene. Ich arbeite
                mit Menschen, die Substanz wollen statt Buzzwords.
              </p>
            </FadeIn>
          </div>
          <div className="lg:col-span-5">
            <FadeIn delay={0.1}>
              <div className="relative aspect-[4/5] rounded-[12px] overflow-hidden border border-white/10">
                <img
                  src={BRAND.photos.portrait}
                  alt={`Portrait von ${BRAND.name}`}
                  className="w-full h-full object-cover img-editorial"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B1324]/70 via-transparent to-transparent" />
              </div>
            </FadeIn>
          </div>
        </div>
      </Section>

      {/* Management Summary */}
      <Section className="bg-[#101B34]">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-4">
            <FadeIn>
              <div className="overline mb-5">Management Summary</div>
              <h2 className="font-display text-4xl lg:text-5xl tracking-[-0.02em] text-white leading-[1.04]">
                Ein Mann aus der Praxis.
              </h2>
            </FadeIn>
          </div>
          <div className="lg:col-span-8 space-y-6">
            <FadeIn delay={0.08}>
              <p className="text-white/75 text-lg leading-relaxed">
                Über zwanzig Jahre Führungspraxis in unterschiedlichsten
                Branchen und Unternehmensgrößen. Fachkenntnisse von
                Unternehmens­führung und HR bis zu Datenschutz, Compliance und
                Cybersecurity.
              </p>
            </FadeIn>
            <FadeIn delay={0.14}>
              <p className="text-white/60 text-[15.5px] leading-relaxed">
                Starker juristischer Unterbau (LL.B. Bachelor of Laws,
                Rheinische Fachhochschule Köln), IHK-Qualifikationen in HR und
                Ausbildung, TÜV-Zertifizierung im Datenschutz. Aktuelle Rolle:
                Geschäftsführer, Gesellschafter und Gründer der{" "}
                <span className="text-white">DSB Business Solutions GmbH</span>.
              </p>
            </FadeIn>
            <FadeIn delay={0.18}>
              <p className="text-white/60 text-[15.5px] leading-relaxed">
                Einsatzgebiet: Deutschland, Österreich, Schweiz, Italien. Der
                rote Faden durch alle Stationen: Klarheit, Umsetzung und
                messbare Ergebnisse.
              </p>
            </FadeIn>
          </div>
        </div>
      </Section>

      {/* Referenz Emde */}
      <Section>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-6">
            <FadeIn>
              <div className="surface p-8 lg:p-10">
                <div className="overline !text-[10px]">Leitreferenz</div>
                <h3 className="mt-3 font-display text-2xl lg:text-[32px] tracking-tight text-white">
                  Interims-Geschäftsführung · Emde Solingen GmbH
                </h3>
                <div className="mt-2 text-white/50 text-[13px] tracking-[0.18em] uppercase">
                  Ende 2023 — Ende 2025
                </div>
                <p className="mt-5 text-white/70 leading-relaxed text-[15.5px]">
                  Als Interims­geschäfts­führer habe ich die Emde Solingen aus
                  einer akuten Krisensituation vor der Insolvenz bewahrt,{" "}
                  <span className="text-white">20 Arbeitsplätze gesichert</span>, das Unternehmen
                  zukunftsorientiert neu aufgestellt und erfolgreich verkauft.
                  Das Mandat wurde mit einer EBIT-Verbesserung von{" "}
                  <span className="text-white">+ 850.000 €</span> abgeschlossen.
                </p>
                <ul className="mt-6 grid sm:grid-cols-2 gap-3 text-[14px] text-white/75">
                  {[
                    "Sanierung & Krisenführung",
                    "Change-Management (Unfreeze · Change · Refreeze)",
                    "Team- und Führungsentwicklung",
                    "Stakeholder- & Bankenkommunikation",
                    "Strategische Neuausrichtung",
                    "M&A-Prozess & Unternehmensverkauf",
                  ].map((x) => (
                    <li key={x} className="flex items-start gap-2">
                      <CheckCircle2
                        size={15}
                        className="mt-0.5 text-brand-400 flex-shrink-0"
                        strokeWidth={1.75}
                      />
                      <span>{x}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </FadeIn>
          </div>
          <div className="lg:col-span-6">
            <FadeIn delay={0.1}>
              <div className="grid grid-cols-2 gap-4">
                <div className="surface p-6">
                  <div className="kpi-number text-4xl lg:text-5xl text-white">+ 850 T€</div>
                  <div className="mt-3 text-[12px] uppercase tracking-[0.18em] text-white/50">EBIT-Wende</div>
                </div>
                <div className="surface p-6">
                  <div className="kpi-number text-4xl lg:text-5xl text-white">20</div>
                  <div className="mt-3 text-[12px] uppercase tracking-[0.18em] text-white/50">Arbeitsplätze gesichert</div>
                </div>
                <div className="surface p-6">
                  <div className="kpi-number text-4xl lg:text-5xl text-white">24 M</div>
                  <div className="mt-3 text-[12px] uppercase tracking-[0.18em] text-white/50">Mandatsdauer</div>
                </div>
                <div className="surface p-6">
                  <div className="kpi-number text-3xl lg:text-4xl text-white">Exit</div>
                  <div className="mt-3 text-[12px] uppercase tracking-[0.18em] text-white/50">Erfolgreicher Verkauf</div>
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
      </Section>

      {/* Unternehmerische Mandate detailliert */}
      <Section className="bg-[#101B34]">
        <SectionHeader
          kicker="Unternehmerische Mandate"
          title="Vier Unternehmen. Eine Handschrift."
          subtitle="Eigene Gründungen, Interimsmandate und aktive Geschäftsführungen – parallel zur Coaching-Tätigkeit."
        />
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6">
          {COMPANIES.map((c, i) => (
            <FadeIn key={c.name} delay={i * 0.06}>
              <div
                data-testid={`about-mandate-${i}`}
                className="surface p-8 h-full flex flex-col"
              >
                <div className="flex items-center justify-between">
                  <div className="overline">{c.type}</div>
                  {c.status === "active" && (
                    <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-brand-400 border border-brand-500/30 rounded-full px-2.5 py-0.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-brand-400" />
                      aktiv
                    </span>
                  )}
                  {c.status === "closed" && (
                    <span className="text-[10px] uppercase tracking-[0.18em] text-white/40 border border-white/10 rounded-full px-2.5 py-0.5">
                      abgeschlossen
                    </span>
                  )}
                  {c.status === "transferred" && (
                    <span className="text-[10px] uppercase tracking-[0.18em] text-white/40 border border-white/10 rounded-full px-2.5 py-0.5">
                      übergeben
                    </span>
                  )}
                </div>

                {c.logo && (
                  <div
                    className={`mt-6 inline-flex items-center h-16 w-fit max-w-[220px] rounded-md overflow-hidden ${
                      c.logoBg === "light"
                        ? "bg-white/95 px-4 py-2 border border-white/20"
                        : "bg-white/[0.03] px-3 py-2 border border-white/10"
                    }`}
                  >
                    <img
                      src={c.logo}
                      alt={`${c.name} Logo`}
                      className="max-h-12 w-auto object-contain"
                      loading="lazy"
                    />
                  </div>
                )}

                <h3 className={`font-display text-[22px] tracking-tight text-white leading-snug ${c.logo ? "mt-5" : "mt-5"}`}>
                  {c.name}
                </h3>
                <div className="mt-1 text-white/55 text-[14px]">{c.role}</div>
                <div className="mt-3 text-white/45 text-[12.5px] tracking-[0.14em] uppercase">
                  {c.period}
                </div>
                <p className="mt-4 text-white/55 text-[14.5px] leading-relaxed">
                  {c.description}
                </p>
                <div className="mt-auto pt-6">
                  <div className="hairline mb-4" />
                  <div className="flex items-center justify-between">
                    <div className="kpi-number text-xl text-white">{c.kpi}</div>
                    {c.url && (
                      <a
                        href={c.url}
                        target="_blank"
                        rel="noreferrer noopener"
                        data-testid={`about-mandate-link-${i}`}
                        className="text-white/60 hover:text-white text-[12.5px] tracking-tight inline-flex items-center gap-1.5"
                      >
                        {c.url.replace(/^https?:\/\//, "")}
                        <ExternalLink size={13} strokeWidth={1.5} />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </FadeIn>
          ))}
        </div>
      </Section>

      {/* Career Timeline */}
      <Section>
        <SectionHeader
          kicker="Werdegang"
          title="Stationen, die geprägt haben."
        />
        <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-3 hidden lg:block">
            <div className="sticky top-32">
              <div className="text-white/45 text-[13px] tracking-[0.2em] uppercase leading-relaxed">
                Auswahl<br />ab 2012
              </div>
            </div>
          </div>
          <div className="lg:col-span-9">
            <ol className="relative border-l border-white/[0.08]">
              {CAREER.map((c, i) => (
                <FadeIn key={`${c.period}-${i}`} delay={i * 0.04}>
                  <li
                    data-testid={`career-item-${i}`}
                    className="relative pl-8 pb-12"
                  >
                    <span className="absolute -left-[7px] top-1.5 w-3.5 h-3.5 rounded-full bg-[#0B1324] border border-brand-500/60" />
                    <div className="overline !text-[10px] mb-2">{c.period}</div>
                    <h3 className="font-display text-xl lg:text-2xl tracking-tight text-white">
                      {c.role}
                    </h3>
                    <div className="mt-1 text-white/55 text-[14px]">{c.org}</div>
                    <ul className="mt-4 space-y-2">
                      {c.bullets.map((b) => (
                        <li
                          key={b}
                          className="flex items-start gap-2 text-white/65 text-[14.5px] leading-relaxed"
                        >
                          <span className="mt-1.5 inline-block w-1 h-1 rounded-full bg-brand-400 flex-shrink-0" />
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                  </li>
                </FadeIn>
              ))}
            </ol>
          </div>
        </div>
      </Section>

      {/* Education / Languages / Industries */}
      <Section className="bg-[#101B34]">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <FadeIn className="lg:col-span-4">
            <div className="surface p-8 h-full">
              <div className="overline">Ausbildung & Zertifikate</div>
              <ul className="mt-6 space-y-5">
                {EDUCATION.map((e) => (
                  <li key={e.title} className="border-b border-white/[0.06] pb-4 last:border-0 last:pb-0">
                    <div className="text-[11.5px] uppercase tracking-[0.18em] text-white/40">
                      {e.year}
                    </div>
                    <div className="mt-1 text-white text-[15.5px] tracking-tight">
                      {e.title}
                    </div>
                    <div className="text-white/45 text-[13px]">{e.place}</div>
                  </li>
                ))}
              </ul>
            </div>
          </FadeIn>

          <FadeIn delay={0.08} className="lg:col-span-4">
            <div className="surface p-8 h-full">
              <div className="overline">Sprachen</div>
              <ul className="mt-6 space-y-5">
                {LANGUAGES.map((l) => (
                  <li
                    key={l.name}
                    className="flex items-center justify-between border-b border-white/[0.06] pb-4 last:border-0 last:pb-0"
                  >
                    <span className="font-display text-lg text-white tracking-tight">
                      {l.name}
                    </span>
                    <span className="text-white/55 text-[13px] tracking-tight">
                      {l.level}
                    </span>
                  </li>
                ))}
              </ul>
              <div className="hairline my-8" />
              <div className="overline">Einsatzgebiet</div>
              <p className="mt-4 text-white/60 text-[14.5px] leading-relaxed">
                Deutschland · Österreich · Schweiz · Italien
              </p>
            </div>
          </FadeIn>

          <FadeIn delay={0.16} className="lg:col-span-4">
            <div className="surface p-8 h-full">
              <div className="overline">Branchenerfahrung</div>
              <ul className="mt-6 space-y-3">
                {INDUSTRIES.map((b) => (
                  <li
                    key={b}
                    className="flex items-start gap-2 text-white/70 text-[14.5px]"
                  >
                    <span className="mt-1.5 inline-block w-1 h-1 rounded-full bg-brand-400 flex-shrink-0" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          </FadeIn>
        </div>
      </Section>

      {/* Speaking image band */}
      <Section padded={false}>
        <FadeIn>
          <div className="relative aspect-[21/9] rounded-[12px] overflow-hidden border border-white/10 my-24 lg:my-32">
            <img
              src={BRAND.photos.stage}
              alt="Alessandro Costantino bei einem Vortrag"
              className="w-full h-full object-cover img-editorial"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B1324]/85 via-[#0B1324]/10 to-transparent" />
            <div className="absolute left-8 bottom-8 right-8 flex items-end justify-between">
              <div>
                <div className="overline">Vortrag · Synaxon Akademie</div>
                <div className="mt-3 font-display text-2xl lg:text-3xl text-white tracking-tight max-w-2xl">
                  „Dauerhaft Geld verdienen am Beispiel Managed Phishing"
                </div>
              </div>
            </div>
          </div>
        </FadeIn>
      </Section>

      {/* Press / BILD article */}
      <Section id="presse" padded={false} className="py-24 lg:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <FadeIn className="lg:col-span-5 order-2 lg:order-1">
            <div className="overline mb-5">In der Presse</div>
            <h2 className="font-display text-4xl lg:text-[56px] tracking-[-0.02em] text-white leading-[1.04]">
              Und ja –
              <br />
              <span className="font-editorial italic text-white/90">
                auch einmal in der BILD.
              </span>
            </h2>
            <p className="mt-6 text-white/65 text-[16px] leading-relaxed">
              {PRESS_ARTICLE.humorLine} Als Geschäftsführer der AWO Service
              & Integration GmbH in Solingen haben wir Menschen mit
              Behinderungen nicht geparkt, sondern ernsthaft beschäftigt – und
              nebenbei bedürftige Familien in der Stadt unterstützt. Die BILD
              hat das 2020 porträtiert.
            </p>
            <blockquote className="mt-8 relative pl-6 border-l-2 border-brand-500/60">
              <p className="font-editorial italic text-white text-[17px] leading-relaxed">
                „{PRESS_ARTICLE.quote}"
              </p>
              <div className="mt-3 text-[12px] uppercase tracking-[0.2em] text-white/45">
                Zitat aus dem BILD-Artikel
              </div>
            </blockquote>
            <div className="hairline my-10" />
            <div className="flex flex-wrap items-center gap-x-8 gap-y-3 text-[13px] tracking-tight text-white/55">
              <span className="overline !text-[10px]">{PRESS_ARTICLE.outlet}</span>
              <span className="w-1 h-1 rounded-full bg-white/20" />
              <span>{PRESS_ARTICLE.date}</span>
              <span className="w-1 h-1 rounded-full bg-white/20" />
              <span>Reportage · Solingen</span>
            </div>
          </FadeIn>

          <FadeIn delay={0.12} className="lg:col-span-7 order-1 lg:order-2">
            <div className="relative group">
              <div className="absolute -inset-4 bg-gradient-to-br from-brand-500/20 via-transparent to-transparent blur-2xl opacity-60" aria-hidden />
              <div
                data-testid="press-article-image"
                className="relative overflow-hidden rounded-[12px] border border-white/10 bg-[#F4F5F7]"
              >
                <img
                  src={PRESS_ARTICLE.image}
                  alt="BILD-Artikel über Alessandro Costantino: Für diesen Chef ist ein Handicap kein Hindernis"
                  className="w-full h-auto block img-editorial"
                  loading="lazy"
                />
                <div className="pointer-events-none absolute inset-0 ring-1 ring-inset ring-black/10" />
              </div>
              <div className="mt-4 text-[12px] tracking-[0.18em] uppercase text-white/35 text-right">
                Ausschnitt · {PRESS_ARTICLE.outlet} · {PRESS_ARTICLE.date}
              </div>
            </div>
          </FadeIn>
        </div>
      </Section>

      {/* Principles */}
      <Section className="bg-[#101B34]">
        <SectionHeader
          kicker="Arbeitsweise"
          title="Vier Prinzipien, keine Kompromisse."
        />
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6">
          {PRINCIPLES.map((p, i) => (
            <FadeIn key={p.title} delay={i * 0.07}>
              <div className="surface p-8 h-full">
                <div className="kpi-number text-[52px] text-white/10 leading-none">
                  0{i + 1}
                </div>
                <h3 className="mt-4 font-display text-xl tracking-tight text-white">
                  {p.title}
                </h3>
                <p className="mt-3 text-white/55 text-[15px] leading-relaxed">
                  {p.text}
                </p>
              </div>
            </FadeIn>
          ))}
        </div>
      </Section>

      {/* CTA */}
      <Section>
        <FadeIn>
          <div className="surface p-10 lg:p-16 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
            <div className="max-w-xl">
              <div className="overline mb-4">Persönlich sprechen</div>
              <h2 className="font-display text-3xl lg:text-4xl tracking-tight text-white leading-[1.1]">
                Das Erstgespräch ist der schnellste Weg, um zu sehen, ob wir
                zusammenpassen.
              </h2>
            </div>
            <Link to="/kontakt" data-testid="about-cta" className="btn-primary">
              Termin anfragen <ArrowUpRight size={18} />
            </Link>
          </div>
        </FadeIn>
      </Section>
    </div>
  );
}
